import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/services/api";
import { Session, TrackConditions } from "@/types";

interface UpdateSessionConditionsRequest {
  sessionId: string;
  conditions: TrackConditions;
}

// Update session conditions
function useUpdateSessionConditions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sessionId,
      conditions,
    }: UpdateSessionConditionsRequest): Promise<Session> => {
      return await apiClient.updateSession(sessionId, { trackConditions: conditions });
    },
    onSuccess: (updatedSession) => {
      // Update the session in the cache
      queryClient.setQueryData(["sessions", updatedSession.id], updatedSession);

      // Invalidate sessions list to refresh any summaries
      queryClient.invalidateQueries({
        queryKey: ["sessions"],
      });
    },
    onError: (error) => {
      console.error("Error updating session conditions:", error);
    },
  });
}

// Combined hook for easier session conditions management
export function useSessionConditionsManager(sessionId: string) {
  const updateMutation = useUpdateSessionConditions();

  const updateConditions = async (conditions: TrackConditions) => {
    try {
      await updateMutation.mutateAsync({ sessionId, conditions });
    } catch (error) {
      console.error("Failed to update session conditions:", error);
      throw error;
    }
  };

  return {
    updateConditions,
    isUpdating: updateMutation.isPending,
    error: updateMutation.error,
  };
}
